<template>
  <div class="flex flex-col items-center">
    <div v-if="showTitle">
      <div v-if="hasTooltip" class="w-full flex items-center justify-center gap-x-1 relative pb-3">
        <h3 class="text-sm text-gray-900 ">{{ $t('time_remaining') }}</h3>
        <!-- Tooltip for desktop -->
        <div @mouseover="openTooltip = true" @mouseleave="openTooltip = false" class="hidden lg:block">
          <img class="w-[13px] h-[13px]" src="../../../assets/img/icons/upload/info.svg" alt="Additional info" />
        </div>
        <!-- Bottom Sheet for mobile -->
        <div @click="showBottomSheet = true" class="block lg:hidden">
          <img class="w-[13px] h-[13px]" src="../../../assets/img/icons/upload/info.svg" alt="Additional info" />
        </div>
        <!-- Tooltip when opened -->
        <div v-if="openTooltip" class="w-[320px] absolute top-8 z-[1]">
          <div
            class="font-body text-base leading-7 text-gray-900 text-left font-normal m-auto lg:m-0 lg:float-right bg-white rounded-lg shadow-ts3 w-full p-5 object-right-bottom">
            {{ $t('no_time_msg') }}
          </div>
        </div>
      </div>
      <h3 v-else class="text-sm text-gray-900 pb-3">{{ $t('time_remaining') }}</h3>
    </div>

    <div class="inline-grid grid-cols-4 w-full gap-x-3" :class="{'!px-3 !pt-2 !pb-1': size == 'small'}" dir="ltr">
      <div class="flex flex-col items-center">
        <div class="inline-grid grid-cols-2 gap-x-1 w-full">
          <div class="border border-gray-500 py-2 text-center rounded-lg" :class="{'!py-0 !border-blue !bg-gray-100 ': size == 'small'}">
            <span class="text-gray-900 font-medium" :class="{'!text-blue': size == 'small'}">{{ daysTens }}</span>
          </div>
          <div class="border border-gray-500 py-2 text-center rounded-lg" :class="{'!py-0 !border-blue !bg-gray-100 ': size == 'small'}">
            <span class="text-gray-900 font-medium" :class="{'!text-blue': size == 'small'}">{{ daysOnes }}</span>
          </div>
        </div>
        <h3 class="text-[10px] text-gray-900 " :class="{'!text-blue': size == 'small'}">{{$t('days')}}</h3>
      </div>
      <div class="flex flex-col items-center">
        <div class="inline-grid grid-cols-2 gap-x-1 w-full">
          <div class="border border-gray-500 py-2 text-center rounded-lg" :class="{'!py-0 !border-blue !bg-gray-100 ': size == 'small'}">
            <span class="text-gray-900 font-medium" :class="{'!text-blue': size == 'small'}">{{ hoursTens }}</span>
          </div>
          <div class="border border-gray-500 py-2 text-center rounded-lg" :class="{'!py-0 !border-blue !bg-gray-100 ': size == 'small'}">
            <span class="text-gray-900 font-medium" :class="{'!text-blue': size == 'small'}">{{ hoursOnes }}</span>
          </div>
        </div>
        <h3 class="text-[10px] text-gray-900 " :class="{'!text-blue': size == 'small'}">{{$t('hours')}}</h3>
      </div>
      <div class="flex flex-col items-center">
        <div class="inline-grid grid-cols-2 gap-x-1 w-full">
          <div class="border border-gray-500 py-2 text-center rounded-lg" :class="{'!py-0 !border-blue !bg-gray-100 ': size == 'small'}">
            <span class="text-gray-900 font-medium" :class="{'!text-blue': size == 'small'}">{{ minutesTens }}</span>
          </div>
          <div class="border border-gray-500 py-2 text-center rounded-lg" :class="{'!py-0 !border-blue !bg-gray-100 ': size == 'small'}">
            <span class="text-gray-900 font-medium" :class="{'!text-blue': size == 'small'}">{{ minutesOnes }}</span>
          </div>
        </div>
        <h3 class="text-[10px] text-gray-900 " :class="{'!text-blue': size == 'small'}">{{$t('min')}}</h3>
      </div>
      <div class="flex flex-col items-center">
        <div class="inline-grid grid-cols-2 gap-x-1 w-full">
          <div class="border border-gray-500 py-2 text-center rounded-lg" :class="{'!py-0 !border-blue !bg-gray-100 ': size == 'small'}">
            <span class="text-gray-900 font-medium" :class="{'!text-blue': size == 'small'}">{{ secondsTens }}</span>
          </div>
          <div class="border border-gray-500 py-2 text-center rounded-lg" :class="{'!py-0 !border-blue !bg-gray-100 ': size == 'small'}">
            <span class="text-gray-900 font-medium" :class="{'!text-blue': size == 'small'}">{{ secondsOnes }}</span>
          </div>
        </div>
        <h3 class="text-[10px] text-gray-900 " :class="{'!text-blue': size == 'small'}">{{$t('sec')}}</h3>
      </div>
    </div>
    <BottomSheet :isOpen="showBottomSheet" :title="$t('no_time_msg_title')" height="auto"
      @close="showBottomSheet = false">
      <p class="text-gray-700">{{ $t('no_time_msg') }}</p>
    </BottomSheet>
  </div>
</template>

<script>
import moment from "moment-timezone";
import BottomSheet from '@/components/reusableComponents/BottomSheet.vue';

export default {
  props: {
    targetDate: String,
    empty: Boolean,
    hasTooltip: Boolean,
    showTitle: {
      type: Boolean,
      default: true
    },
    size: {
      type: String,
      default: "normal" // or small for leaderboard
    }
  },
  components: { BottomSheet },
  data() {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      intervalId: null,
      openTooltip: false,
      showBottomSheet: false
    };
  },
  computed: {
    // Split days into two variables
    daysTens() {
      return Math.floor(this.days / 10);
    },
    daysOnes() {
      return this.days % 10;
    },
    // Split hours into two variables
    hoursTens() {
      return Math.floor(this.hours / 10);
    },
    hoursOnes() {
      return this.hours % 10;
    },
    // Split minutes into two variables
    minutesTens() {
      return Math.floor(this.minutes / 10);
    },
    minutesOnes() {
      return this.minutes % 10;
    },
    // Split seconds into two variables
    secondsTens() {
      return Math.floor(this.seconds / 10);
    },
    secondsOnes() {
      return this.seconds % 10;
    },
  },
  methods: {
    updateCountdown() {
      const now = moment.tz("Europe/Sofia");
      const target = moment.tz(this.targetDate, "Europe/Sofia");
      const duration = moment.duration(target.diff(now));

      // If countdown is finished
      if (duration.asSeconds() <= 0) {
        clearInterval(this.intervalId);
        this.days = this.hours = this.minutes = this.seconds = 0;
        return;
      }

      // Calculate remaining days, hours, minutes, and seconds
      this.days = Math.floor(duration.asDays());
      this.hours = duration.hours();
      this.minutes = duration.minutes();
      this.seconds = duration.seconds();
    },
    startCountdown() {
      this.updateCountdown();
      this.intervalId = setInterval(() => {
        this.updateCountdown();
      }, 1000);
    },
  },
  mounted() {
    if (this.empty) {
      this.days = 0;
      this.hours = 0;
      this.minutes = 0;
      this.seconds = 0;
    } else this.startCountdown();
  },
  beforeDestroy() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  },
};
</script>
